import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API, formatApiError } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Download, CreditCard, Check, Loader2, RefreshCw, AlertCircle, Share2 } from "lucide-react";
import { toast } from "sonner";

const EncodingPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [presets, setPresets] = useState({});
  const [selectedPresets, setSelectedPresets] = useState([]);
  const [encodingJobs, setEncodingJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [encoding, setEncoding] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [poNumber, setPoNumber] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [superless, setSuperless] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [projectRes, presetsRes, jobsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/encoding/presets`),
        axios.get(`${API}/projects/${projectId}/encoding-jobs`)
      ]);
      setProject(projectRes.data);
      setPresets(presetsRes.data);
      setEncodingJobs(jobsRes.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Poll for in-progress jobs
  useEffect(() => {
    const pending = encodingJobs.filter(j => j.status === "processing" || j.status === "queued");
    if (!pending.length) return;
    const interval = setInterval(async () => {
      try {
        const jobsRes = await axios.get(`${API}/projects/${projectId}/encoding-jobs`);
        setEncodingJobs(jobsRes.data);
        if (!jobsRes.data.some(j => j.status === "processing" || j.status === "queued")) {
          clearInterval(interval);
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [encodingJobs, projectId]);

  const togglePreset = (key) => setSelectedPresets(prev => prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]);

  const validateDiscount = async () => {
    if (!discountCode) return;
    try {
      const formData = new FormData();
      formData.append("code", discountCode);
      const res = await axios.post(`${API}/validate-discount`, formData);
      setDiscount(res.data.discount_percent);
      toast.success(`${res.data.discount_percent}% discount applied`);
    } catch { toast.error("Invalid code"); setDiscount(0); }
  };

  const calculateCost = () => {
    const hasOther = project?.aspect_ratios?.length > 0 || project?.graphics?.length > 0;
    let cost = hasOther ? Math.max(0, (selectedPresets.length - 1) * 0.99) : selectedPresets.length * 0.99;
    if (discount > 0) cost *= (1 - discount / 100);
    return cost.toFixed(2);
  };

  const startEncoding = async () => {
    if (selectedPresets.length === 0) return;
    setEncoding(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/encode`, {
        project_id: projectId,
        presets: selectedPresets,
        superless
      });
      setEncodingJobs([res.data, ...encodingJobs]);
      toast.success("Encoding started - this may take a few minutes");
      setSelectedPresets([]);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setEncoding(false); }
  };

  const proceedToPayment = async () => {
    const cost = parseFloat(calculateCost());
    if (cost <= 0) {
      startEncoding();
      return;
    }
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        project_id: projectId,
        service_type: "encoding",
        items: selectedPresets.map(p => ({ preset: p })),
        po_number: poNumber,
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const createShareLink = async () => {
    setSharing(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/share`);
      const link = `${window.location.origin}/shared/${res.data.share_token}`;
      setShareLink(link);
      navigator.clipboard?.writeText(link);
      toast.success("Share link copied to clipboard");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSharing(false); }
  };

  const downloadOutput = (outputId) => {
    window.open(`${API}/files/${outputId}/download`, "_blank");
  };

  const groups = {
    social: { title: "Social Media", keys: ["facebook", "instagram", "instagram_story", "youtube", "tiktok", "twitter", "linkedin"] },
    broadcast: { title: "Broadcast & Professional", keys: ["prores_master", "broadcast_hd", "h264_web"] }
  };

  const completedJobs = encodingJobs.filter(j => j.status === "completed");
  const pendingJobs = encodingJobs.filter(j => j.status === "processing" || j.status === "queued");
  const allOutputs = completedJobs.flatMap(j => j.outputs || []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />
      <Loader2 className="w-6 h-6 animate-spin text-cyan-400/40" />
    </div>
  );

  return (
    <div className="min-h-screen" data-testid="encoding-page">
      <div className="fixed inset-0 bg-gradient-hero -z-10" />

      <header className="border-b border-cyan-500/10 backdrop-blur-sm bg-[#020a14]/60">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <button onClick={() => navigate(`/project/${projectId}`)} className="text-cyan-400/40 hover:text-cyan-300 transition-colors" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="font-['Rajdhani'] text-lg tracking-wide">ENCODE & EXPORT</h1>
              <p className="text-sm text-cyan-200/30">{project?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {allOutputs.length > 0 && (
              <Button variant="outline" onClick={createShareLink} className="h-10 px-4 rounded-sm btn-outline-blue font-['Rajdhani'] tracking-wider text-sm" disabled={sharing} data-testid="share-btn">
                <Share2 className="w-4 h-4 mr-2" />{sharing ? "..." : "SHARE"}
              </Button>
            )}
            <span className="text-sm text-cyan-400/60 font-mono">${calculateCost()}</span>
            <Button onClick={proceedToPayment} className="h-10 px-6 rounded-sm btn-primary font-['Rajdhani'] tracking-wider text-sm" disabled={selectedPresets.length === 0 || encoding} data-testid="encode-btn">
              {encoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <>ENCODE ({selectedPresets.length})</>}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
        {/* Share link display */}
        {shareLink && (
          <div className="mb-8 p-4 card-glass rounded-sm flex items-center gap-4">
            <Share2 className="w-4 h-4 text-cyan-400/60 shrink-0" />
            <input type="text" readOnly value={shareLink} className="flex-1 bg-transparent text-sm text-cyan-200/60 outline-none" data-testid="share-link-input" />
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(shareLink); toast.success("Copied"); }} className="rounded-sm btn-outline-blue text-xs">Copy</Button>
          </div>
        )}

        {/* Active encoding jobs */}
        {pendingJobs.length > 0 && (
          <div className="mb-12">
            <h2 className="text-sm text-cyan-400/60 mb-4 tracking-widest uppercase flex items-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" /> Encoding in Progress
            </h2>
            {pendingJobs.map(job => (
              <div key={job.id} className="card-glass rounded-sm p-6 mb-4" data-testid={`active-job-${job.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-cyan-200/60">Processing {job.presets?.length} preset(s)...</span>
                  <span className="text-sm text-cyan-200/30">{new Date(job.created_at).toLocaleTimeString()}</span>
                </div>
                <Progress value={50} className="h-1" />
              </div>
            ))}
          </div>
        )}

        {/* Completed downloads */}
        {allOutputs.length > 0 && (
          <div className="mb-12">
            <h2 className="text-sm text-cyan-400/60 mb-4 tracking-widest uppercase">Ready for Download</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {allOutputs.map(out => (
                <div key={out.id} className="card-glass rounded-sm p-4 flex items-center justify-between" data-testid={`output-${out.id}`}>
                  <div>
                    <p className="text-cyan-200/80 capitalize font-['Rajdhani'] tracking-wide">{out.preset?.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-cyan-200/30">
                      {out.aspect_ratio && out.aspect_ratio !== "original" ? `${out.aspect_ratio} - ` : ""}
                      {out.size ? `${(out.size / 1024 / 1024).toFixed(1)} MB` : out.format?.toUpperCase()}
                    </p>
                  </div>
                  <button onClick={() => downloadOutput(out.id)} className="p-2 text-cyan-400/60 hover:text-cyan-300 transition-colors" data-testid={`download-${out.id}`}>
                    <Download className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Presets selection */}
          <div className="lg:col-span-2 space-y-12">
            {Object.entries(groups).map(([key, group]) => (
              <div key={key}>
                <h2 className="text-sm text-cyan-400/60 mb-6 tracking-widest uppercase">{group.title}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {group.keys.map(pk => {
                    const preset = presets[pk];
                    if (!preset) return null;
                    return (
                      <label key={pk} className={`flex items-center gap-4 p-4 border rounded-sm cursor-pointer transition-all ${selectedPresets.includes(pk) ? 'border-cyan-500/40 bg-cyan-500/[0.05] shadow-[0_0_15px_rgba(0,180,255,0.1)]' : 'border-cyan-500/10 hover:border-cyan-500/20'}`} data-testid={`preset-${pk}`}>
                        <Checkbox checked={selectedPresets.includes(pk)} onCheckedChange={() => togglePreset(pk)} />
                        <div className="flex-1">
                          <p className="font-['Rajdhani'] tracking-wide">{preset.name}</p>
                          <p className="text-sm text-cyan-200/30">{preset.width}x{preset.height} - {preset.format.toUpperCase()} - {preset.bitrate}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="card-glass rounded-sm p-6 space-y-4">
              <h3 className="text-sm text-cyan-400/60 tracking-widest uppercase">Options</h3>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox checked={superless} onCheckedChange={setSuperless} data-testid="superless-checkbox" />
                <div>
                  <p className="text-sm text-cyan-200/60">Superless Export</p>
                  <p className="text-xs text-cyan-200/30">Export without text overlays</p>
                </div>
              </label>
            </div>

            <div className="card-glass rounded-sm p-6 space-y-4">
              <h3 className="text-sm text-cyan-400/60 tracking-widest uppercase">Discount Code</h3>
              <div className="flex gap-2">
                <Input placeholder="Code" value={discountCode} onChange={e => setDiscountCode(e.target.value.toUpperCase())} className="h-10 rounded-sm" data-testid="discount-code-input" />
                <Button variant="outline" onClick={validateDiscount} className="h-10 rounded-sm btn-outline-blue" data-testid="apply-discount-btn">Apply</Button>
              </div>
              {discount > 0 && <p className="text-sm text-green-400">{discount}% applied</p>}
            </div>

            <div className="card-glass rounded-sm p-6 space-y-4">
              <h3 className="text-sm text-cyan-400/60 tracking-widest uppercase">PO / Reference</h3>
              <Input placeholder="PO or job number" value={poNumber} onChange={e => setPoNumber(e.target.value)} className="h-10 rounded-sm" data-testid="po-number-input" />
            </div>

            <div className="card-glass rounded-sm p-6">
              <h3 className="text-sm text-cyan-400/60 mb-4 tracking-widest uppercase">Cost Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-cyan-200/40"><span>Selected presets</span><span>{selectedPresets.length}</span></div>
                <div className="flex justify-between text-cyan-200/40"><span>Per encode</span><span>$0.99</span></div>
                {(project?.aspect_ratios?.length > 0 || project?.graphics?.length > 0) && (
                  <div className="flex justify-between text-green-400"><span>First encode</span><span>FREE</span></div>
                )}
                {discount > 0 && <div className="flex justify-between text-green-400"><span>Discount</span><span>-{discount}%</span></div>}
                <div className="flex justify-between pt-4 border-t border-cyan-500/10 text-lg font-['Rajdhani'] tracking-wide">
                  <span>Total</span>
                  <span className="text-cyan-300">${calculateCost()}</span>
                </div>
              </div>
            </div>

            {/* Project summary */}
            <div className="card-glass rounded-sm p-6">
              <h3 className="text-sm text-cyan-400/60 mb-4 tracking-widest uppercase">Project</h3>
              <div className="space-y-2 text-sm">
                {project?.video_duration && <div className="flex justify-between text-cyan-200/40"><span>Duration</span><span>{Math.round(project.video_duration)}s</span></div>}
                {project?.aspect_ratios?.length > 0 && <div className="flex justify-between text-cyan-200/40"><span>Aspect Ratios</span><span>{project.aspect_ratios.join(", ")}</span></div>}
                {project?.graphics?.length > 0 && <div className="flex justify-between text-cyan-200/40"><span>Overlays</span><span>{project.graphics.length}</span></div>}
                {project?.audio_tracks?.length > 0 && <div className="flex justify-between text-green-400/60"><span>Audio</span><span>Replaced</span></div>}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EncodingPage;
